import { DatePipe, NgTemplateOutlet } from '@angular/common';
import {
  Component,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { filter } from 'rxjs';
import { HandleLoadingState } from '../../../decorators/handle-loading-state.decorator';
import { DateEx, WEEKDAYS } from '../../../functions/date-ex';
import {
  filterDiariesByDate,
  formatThreeMonthRange,
  getClaimSpecificFields,
  getDefaultThreeMonthAnchor,
  mapDiariesToTableRows,
} from '../../../functions/todo/diary-mapper';
import { SearchDiariesRequest } from '../../../models/api/todo/client-diaries/search-diaries-request';
import { Action } from '../../../models/core/action';
import { FormatPipe } from '../../../pipes/format.pipe';
import { DiaryCompleteService } from '../../../services/todo/diary-complete.service';
import { BaseComponent } from '../../base/base.component';
import { IconButtonComponent } from '../../core/buttons/icon-button.component';
import { RadioButtonComponent } from '../../core/controls/radio-button.component';
import { ToggleSwitchComponent } from '../../core/controls/toggle-switch.component';
import { PanelComponent } from '../../core/panels/panel.component';
import {
  DIARY_PREF_PERIOD_CLAIM,
  DIARY_PREF_PERIOD_MAIN,
  DIARY_PREF_TYPE,
  DiaryPeriodMode,
} from './diary-storage.keys';
import { DiaryTableTopComponent } from './diary-table-top.component';
import { DiaryThreeMonthComponent } from './diary-three-month.component';
import { DiaryWeekComponent } from './diary-week.component';
import { SharedDiaryPrefDialogComponent } from './shared-diary-pref-dialog.component';

@Component({
  selector: 'app-diary-my-diaries',
  imports: [
    NgTemplateOutlet,
    ReactiveFormsModule,
    RadioButtonComponent,
    DiaryWeekComponent,
    DiaryThreeMonthComponent,
    ToggleSwitchComponent,
    IconButtonComponent,
    PanelComponent,
    DiaryTableTopComponent,
    SharedDiaryPrefDialogComponent,
  ],
  providers: [DatePipe, FormatPipe],
  template: `
    <div class="tw-pb-4">
      <app-radio-button
        [control]="periodControl"
        label="View my open diaries for:"
        optionTitle="title"
        optionLabel="label"
        optionValue="key"
        [options]="periodOptions"
        [vertical]="false"
        (change)="onPeriodChange()"
      />
    </div>

    <ng-template #prefsGear>
      <app-icon-button
        diaryToolbarRight
        [border]="false"
        ariaLabel="Shared diaries preferences"
        tooltip="Shared diaries preferences"
        icon="fa-light fa-gear"
        (click)="openPreferences()"
      />
    </ng-template>

    @if (periodReady && periodControl.value !== 'all') {
      @if (periodControl.value === 'week') {
        <app-diary-week
          [items]="filteredCalendarItems"
          [loading]="tableLoading && !selectedDate"
          (includeCompletedChange)="onIncludeCompletedChange($event)"
          (calendarAction)="onWeekCalendarAction($event)"
        >
          <ng-container
            *ngTemplateOutlet="prefsGear"
            ngProjectAs="[diaryToolbarRight]"
          />
        </app-diary-week>
      } @else {
        <app-diary-three-month
          [diaryData]="diaryData"
          [loading]="tableLoading && !selectedDate"
          [selectedDate]="selectedDate"
          (includeCompletedChange)="onIncludeCompletedChange($event)"
          (calendarAction)="onThreeMonthCalendarAction($event)"
        >
          <ng-container
            *ngTemplateOutlet="prefsGear"
            ngProjectAs="[diaryToolbarRight]"
          />
        </app-diary-three-month>
      }
    } @else {
      <div class="tw-flex tw-items-center tw-justify-end tw-gap-1 tw-pb-4">
        <app-toggle-switch
          [control]="allIncludeControl"
          label="Include completed"
          [showHelpText]="false"
        />
        <ng-container *ngTemplateOutlet="prefsGear" />
      </div>
    }

    <app-panel>
      <app-diary-table-top
        [diaryEntries]="tableRows"
        [isPageTodo]="isPageTodo"
        [loading]="tableLoading"
        [error]="error"
        [showSelection]="true"
        [showActionButtons]="true"
        [showPageOfTotal]="periodControl.value === 'all'"
        [recordPeriodText]="recordPeriodText"
        (markCompleteEvent)="onMarkComplete($event)"
        (markSelectedCompleteEvent)="onMarkComplete($event)"
        (markAllCompleteEvent)="onMarkComplete($event)"
      />
    </app-panel>

    <app-shared-diary-pref-dialog #prefDialog />
  `,
  styles: ``,
})
export class DiaryMyDiariesComponent extends BaseComponent implements OnInit {
  private formatPipe = inject(FormatPipe);
  private diaryComplete = inject(DiaryCompleteService);

  @ViewChild('prefDialog') prefDialog?: SharedDiaryPrefDialogComponent;

  isPageTodo = false;
  claim: any = null;
  tableLoading = false;
  error: any = null;
  includeCompleted = false;
  selectedDate: string | null = null;
  /** Calendar stays unmounted until the saved period is known. */
  periodReady = false;
  recordPeriodText = '';
  private diaryLoadGeneration = 0;
  tableRows: any[] = [];

  periodControl = new FormControl<DiaryPeriodMode>('week');
  allIncludeControl = new FormControl(false);

  periodOptions: Action[] = [];

  diaryData: any[] = [];
  selectedDayData: any[] = [];
  calendarItems: any[] = [];
  weekStartDate: Date;
  weekEndDate: Date;
  threeMonthAnchor = getDefaultThreeMonthAnchor();
  diaryRequest: SearchDiariesRequest;

  constructor() {
    super();
    let previousUrl = this.router.url ?? '';
    this.dispose.subs = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(async (event) => {
        if (previousUrl.startsWith('/diary-edit')) {
          await this.refreshDiaries();
        }
        previousUrl = event.urlAfterRedirects ?? event.url;
      });
  }

  async ngOnInit(): Promise<void> {
    this.isPageTodo = document.location.pathname === '/todo';
    this.claim = this.view.currentClaim();
    this.periodOptions = this.isPageTodo
      ? [
          { label: 'The week', key: 'week', title: 'The week' },
          { label: '3 months', key: 'three-month', title: '3 months' },
        ]
      : [
          { label: 'All', key: 'all', title: 'All' },
          { label: 'The week', key: 'week', title: 'The week' },
          { label: '3 months', key: 'three-month', title: '3 months' },
        ];

    await this.loadPeriodPreference();
    this.periodReady = true;

    if (this.periodControl.value === 'all') {
      this.includeCompleted = !!this.allIncludeControl.value;
      await this.loadAllDiaries();
    }

    this.dispose.subs = this.allIncludeControl.valueChanges.subscribe(
      (value) => {
        this.includeCompleted = !!value;
        if (this.periodControl.value === 'all') {
          void this.loadAllDiaries();
          return;
        }
        this.rebuildTableRows();
      },
    );
  }

  get filteredCalendarItems(): any[] {
    const items = this.calendarItems.map((item) => {
      if (!this.includeCompleted) {
        return { ...item, completed: 0 };
      }
      return item;
    });
    if (this.selectedDate) {
      for (const item of items) {
        item.active =
          DateEx.formatShortDate(new Date(item.date)) === this.selectedDate;
      }
    }
    return items;
  }

  private rebuildTableRows(): void {
    let source = this.diaryData;
    if (this.selectedDate) {
      source = this.selectedDayData;
    }
    this.tableRows = mapDiariesToTableRows(source, this.formatPipe, {
      isPageTodo: this.isPageTodo,
      claim: this.claim,
      includeCompleted: this.includeCompleted,
    });
    this.updateRecordPeriodSuffix();
  }

  /** Period/date suffix only; page counts come from the table paginator. */
  private updateRecordPeriodSuffix(): void {
    if (this.periodControl.value === 'all') {
      this.recordPeriodText = '';
      return;
    }
    if (this.selectedDate) {
      this.recordPeriodText = this.selectedDate;
      return;
    }
    if (this.periodControl.value === 'three-month') {
      this.recordPeriodText = formatThreeMonthRange(this.threeMonthAnchor);
      return;
    }
    if (this.weekStartDate && this.weekEndDate) {
      this.recordPeriodText = `${DateEx.formatShortDate(this.weekStartDate)} – ${DateEx.formatShortDate(this.weekEndDate)}, ${this.weekEndDate.getFullYear()}`;
      return;
    }
    this.recordPeriodText = '';
  }

  private clearDateSelection(): void {
    this.selectedDate = null;
    this.selectedDayData = [];
  }

  private applySelectedDateFilter(): void {
    if (!this.selectedDate) {
      return;
    }
    this.selectedDayData = (this.diaryData ?? []).filter((diary: any) => {
      return (
        DateEx.formatShortDate(new Date(diary.dateDue)) === this.selectedDate
      );
    });
  }

  async onPeriodChange(): Promise<void> {
    await this.savePeriodPreference();
    this.clearDateSelection();
    if (this.periodControl.value === 'all') {
      this.includeCompleted = !!this.allIncludeControl.value;
      await this.loadAllDiaries();
    } else if (this.periodControl.value === 'three-month') {
      this.threeMonthAnchor = getDefaultThreeMonthAnchor();
    }
  }

  onIncludeCompletedChange(value: boolean): void {
    this.includeCompleted = value;
    if (this.periodControl.value === 'all') {
      this.allIncludeControl.setValue(value, { emitEvent: false });
    }
    this.rebuildTableRows();
  }

  onWeekCalendarAction(event: {
    action: string;
    weekStartDate: Date;
    weekEndDate: Date;
  }): void {
    this.tableLoading = true;
    this.weekStartDate = DateEx.getStartOfWeek(event.weekStartDate);
    this.weekEndDate = DateEx.getEndOfWeek(this.weekStartDate);
    this.diaryRequest = this.prepareRequest(this.weekStartDate, this.weekEndDate);

    switch (event.action) {
      case 'calendarClick': {
        const clickedDate = new Date(event.weekStartDate);
        this.selectedDate = DateEx.formatShortDate(clickedDate);
        this.selectedDayData = filterDiariesByDate(this.diaryData, clickedDate);
        this.tableLoading = false;
        this.rebuildTableRows();
        break;
      }
      case 'init':
      case 'today':
      case 'previous':
      case 'next':
        this.clearDateSelection();
        void this.getDiaryEntries();
        break;
      default:
        this.tableLoading = false;
        break;
    }
  }

  onThreeMonthCalendarAction(event: {
    action: string;
    periodAnchor: Date;
    startDate: Date;
    endDate: Date;
    selectedDate?: Date;
  }): void {
    this.threeMonthAnchor = event.periodAnchor;
    this.tableLoading = true;
    this.diaryRequest = this.prepareRequest(event.startDate, event.endDate);

    if (event.action === 'calendarClick' && event.selectedDate) {
      this.selectDiaryDate(event.selectedDate);
      this.tableLoading = false;
      this.rebuildTableRows();
      return;
    }

    this.clearDateSelection();
    void this.getDiaryEntries();
  }

  private selectDiaryDate(date: Date): void {
    this.selectedDate = DateEx.formatShortDate(date);
    this.selectedDayData = filterDiariesByDate(this.diaryData, date);
  }

  prepareRequest(
    startDate: Date | null,
    endDate: Date | null,
  ): SearchDiariesRequest {
    const request: SearchDiariesRequest = {
      diaryStatus: 0,
      dueDateStart: startDate,
      dueDateEnd: endDate,
      duePast: false,
    };
    if (this.isPageTodo) {
      request.followUpBy = this.session.user.userName;
    } else if (this.claim?.recordId != null) {
      Object.assign(request, getClaimSpecificFields(this.claim));
      if (this.periodControl.value === 'all') {
        request.diaryStatus = this.includeCompleted ? 0 : 1;
      }
    }
    return request;
  }

  @HandleLoadingState({ loadingProperty: 'tableLoading', useViewLoading: true })
  async getDiaryEntries(): Promise<void> {
    const generation = ++this.diaryLoadGeneration;
    const data = await this.api.clientDiary.getDiaryEntries(this.diaryRequest);
    if (generation !== this.diaryLoadGeneration) {
      return;
    }
    this.onDiaryDataReceived(data);
  }

  async refreshDiaries(): Promise<void> {
    if (this.periodControl.value === 'all') {
      await this.loadAllDiaries();
      return;
    }
    if (this.diaryRequest) {
      await this.getDiaryEntries();
    }
  }

  async loadAllDiaries(): Promise<void> {
    this.tableLoading = true;
    this.diaryRequest = this.prepareRequest(null, null);
    await this.getDiaryEntries();
  }

  onDiaryDataReceived(data: any[]): void {
    this.diaryData = data;
    if (this.selectedDate) {
      this.applySelectedDateFilter();
    }
    if (this.periodControl.value === 'week') {
      this.calendarItems = this.prepareWeekCalendarDataSource();
    }
    this.tableLoading = false;
    this.rebuildTableRows();
  }

  prepareWeekCalendarDataSource(): any[] {
    if (!this.weekStartDate) {
      return [];
    }
    const weekMap: Record<string, any> = {};
    for (let i = 0; i < 7; i++) {
      const date = new Date(this.weekStartDate);
      date.setDate(date.getDate() + i);
      const dayKey = WEEKDAYS[date.getDay()];
      weekMap[dayKey] = {
        day: dayKey,
        date: date.toISOString(),
        displayDate: DateEx.formatShortDate(date),
        completed: 0,
        pastDue: 0,
        draft: 0,
        open: 0,
      };
    }
    const dataSource = this.diaryData;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startOfWeek = new Date(this.weekStartDate);
    startOfWeek.setHours(0, 0, 0, 0);
    const endOfWeek = new Date(this.weekEndDate);
    endOfWeek.setHours(23, 59, 59, 999);

    for (const diary of dataSource) {
      const due = new Date(diary.dateDue);
      if (due < startOfWeek || due > endOfWeek) {
        continue;
      }
      const dayKey = WEEKDAYS[due.getDay()];
      const item = weekMap[dayKey];
      if (!item) continue;
      const dueDateOnly = new Date(
        due.getFullYear(),
        due.getMonth(),
        due.getDate(),
      );
      const status = (diary.status || '').toLowerCase();
      if (dueDateOnly < today && status !== 'completed') {
        item.pastDue++;
      } else if (status === 'completed') {
        item.completed++;
      } else if (status === 'open') {
        item.open++;
      }
    }
    return Object.values(weekMap);
  }

  onMarkComplete(ids: number | number[]): void {
    const diaryIds = Array.isArray(ids) ? ids : [ids];
    this.diaryComplete.confirmAndMarkComplete(diaryIds, () =>
      this.afterMarkComplete(),
    );
  }

  openPreferences(): void {
    void this.prefDialog?.show();
  }

  private async afterMarkComplete(): Promise<void> {
    await this.refreshDiaries();
    if (this.selectedDate) {
      this.selectedDayData = filterDiariesByDate(
        this.diaryData,
        new Date(this.selectedDate),
      );
      this.rebuildTableRows();
    }
  }

  private async loadPeriodPreference(): Promise<void> {
    const key = this.isPageTodo
      ? DIARY_PREF_PERIOD_MAIN
      : DIARY_PREF_PERIOD_CLAIM;
    const defaultPeriod: DiaryPeriodMode = this.isPageTodo ? 'week' : 'all';
    try {
      const prefs = await this.api.preferences.getPreferences({
        type: DIARY_PREF_TYPE,
        key,
        value: '',
      });
      const value = prefs?.[0]?.value as DiaryPeriodMode;
      if (value && this.periodOptions.some((o) => o.key === value)) {
        this.periodControl.setValue(value, { emitEvent: false });
      } else {
        this.periodControl.setValue(defaultPeriod, { emitEvent: false });
      }
    } catch {
      this.periodControl.setValue(defaultPeriod, { emitEvent: false });
    }
  }

  private async savePeriodPreference(): Promise<void> {
    const key = this.isPageTodo
      ? DIARY_PREF_PERIOD_MAIN
      : DIARY_PREF_PERIOD_CLAIM;
    const value = this.periodControl.value;
    if (!value) return;
    try {
      await this.api.preferences.setPreference({
        type: DIARY_PREF_TYPE,
        key,
        value,
      });
    } catch {
      // Preference save is best-effort.
    }
  }
}
